import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Plus, Film, ChevronRight, Eye } from 'lucide-react';

interface Movie {
  id: string;
  title: string;
  description: string | null;
  status: string;
  type: string;
  poster_url: string | null;
  view_count: number;
  created_at: string;
}

export default function MovieList() {
  const { publisher } = useAuth();
  const navigate = useNavigate();
  const [movies, setMovies] = useState<Movie[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (publisher?.id) fetchMovies();
  }, [publisher?.id]);

  const fetchMovies = async () => {
    if (!publisher?.id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('works')
        .select('id, title, description, status, type, poster_url, view_count, created_at')
        .eq('publisher_id', publisher.id)
        .in('type', ['movie', 'short_film', 'documentary'])
        .neq('status', 'archived')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setMovies((data || []) as unknown as Movie[]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Movies</h1>
          <p className="text-muted-foreground mt-1">Manage your movies, short films, and documentaries</p>
        </div>
        <Button onClick={() => navigate('/content/movies/new')}>
          <Plus className="h-4 w-4 mr-2" />
          New Title
        </Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : movies.length === 0 ? (
        <Card className="partner-card">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Film className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">No movies yet</h3>
              <p className="text-muted-foreground text-sm mt-1">
                Create a title to start — you'll attach a video after.
              </p>
            </div>
            <Button onClick={() => navigate('/content/movies/new')}>
              <Plus className="h-4 w-4 mr-2" />
              New Title
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {movies.map(movie => (
            <Card key={movie.id} className="partner-card kinora-editorial-card">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {movie.poster_url ? (
                      <img
                        src={movie.poster_url}
                        alt={movie.title}
                        className="h-14 w-10 rounded object-cover shrink-0"
                      />
                    ) : (
                      <div className="h-14 w-10 rounded bg-muted flex items-center justify-center shrink-0">
                        <Film className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="kinora-eyebrow">{movie.type.replace('_', ' ')}</p>
                      <CardTitle className="font-display text-lg truncate mt-1">{movie.title}</CardTitle>
                      {movie.description && (
                        <CardDescription className="mt-1 line-clamp-2 font-display italic">
                          {movie.description}
                        </CardDescription>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {movie.type !== 'movie' && (
                      <Badge variant="secondary" className="capitalize">{movie.type.replace('_', ' ')}</Badge>
                    )}
                    <Badge variant="outline" className="capitalize">{movie.status}</Badge>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Eye className="h-3.5 w-3.5" />
                      <span>{(movie.view_count || 0).toLocaleString()}</span>
                    </div>
                    <Button variant="ghost" size="sm" asChild>
                      <Link to={`/content/movies/${movie.id}`}>
                        Manage
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
